import {
  ActionIcon,
  Divider,
  Group,
  Stack,
  Text,
  TextInput,
  TextInputProps,
  useMantineTheme,
} from '@mantine/core';
import { IconArrowLeft, IconArrowRight, IconSearch } from '@sloyd/icons';
import { Button } from '../Button/Button';
import { Items, Wrapper } from './PageStory.style';

interface CustomDividerProps {
  label: string;
}

const CustomDivider = ({ label }: CustomDividerProps) => (
  <Divider
    label={label}
    orientation='horizontal'
    w='70vw'
    labelPosition='left'
    styles={(theme) => ({
      root: {
        borderBottom: `1px solid ${theme.colors['whitesmoke'][5]}`,
      },
      label: {
        fontSize: 16,
        color: theme.colors.whitesmoke[5],
      },
      inner: {
        borderTop: 'none', // Remove the middle line
      },
    })}
  />
);

const ColorBox = ({ colorName, shade, displayName, usage }: any) => {
  const theme = useMantineTheme();

  return (
    <div
      style={{
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        marginBottom: '8px',
      }}
    >
      <div
        style={{
          width: '40px',
          height: '40px',
          backgroundColor: theme.colors[colorName][shade],
          borderRadius: '4px',
        }}
      />
      <div>
        <p style={{ margin: 0, fontWeight: '500' }}>{displayName}</p>
        <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>{usage}</p>
      </div>
    </div>
  );
};

export type PageStoryProps = {};

export function PageStory(_props: PageStoryProps) {
  return (
    <Wrapper className='PageStory-wrapper' data-testid='PageStory-wrapper'>
      <h1>Sloyd UI</h1>
      <h2>Foundations</h2>
      <h3>Colors</h3>
      <section>
        <h4>Brand Colors</h4>
        <ColorBox
          colorName='brand-cyan'
          shade={5}
          displayName='Brand Cyan'
          usage='Primary brand color, use to emphasis ui componenets'
        />
        <ColorBox
          colorName='pink'
          shade={5}
          displayName='Brand Pink'
          usage='Secondary brand color (#FF29A8)'
        />
        <ColorBox
          colorName='brand-green'
          shade={5}
          displayName='Brand Green'
          usage='Tertiary brand color (#03F584)'
        />
        <h4>Feedback Colors</h4>
        <ColorBox
          colorName='feedback'
          shade={0}
          displayName='Amazonite 100'
          usage='Success states, positive actions'
        />
        <ColorBox
          colorName='feedback'
          shade={1}
          displayName='Amazonite 15'
          usage='Success background states'
        />
        <ColorBox
          colorName='feedback'
          shade={2}
          displayName='Amber 100'
          usage='Warning states, needs attention'
        />
        <ColorBox
          colorName='feedback'
          shade={3}
          displayName='Amber 15'
          usage='Warning background states'
        />
        <ColorBox
          colorName='feedback'
          shade={4}
          displayName='Jasper 100'
          usage='Error states, destructive actions'
        />
        <ColorBox
          colorName='feedback'
          shade={5}
          displayName='Jasper 15'
          usage='Error background states'
        />
        <ColorBox
          colorName='feedback'
          shade={7}
          displayName='Lapis 50'
          usage='Use as subtle borders'
        />
        <ColorBox
          colorName='feedback'
          shade={6}
          displayName='Lapis 20'
          usage='Information states'
        />

        <h4>Utility Colors</h4>
        <ColorBox colorName='utility' shade={9} displayName='Topaz 100' usage='Primary on hover' />
        <ColorBox
          colorName='utility'
          shade={5}
          displayName='Sodalite 100'
          usage='Primary backgrounds'
        />
        <ColorBox
          colorName='utility'
          shade={8}
          displayName='Midnight 100'
          usage='Deep dark backgrounds'
        />

        <h4>Text Colors</h4>
        <ColorBox
          colorName='whitesmoke'
          shade={0}
          displayName='Whitesmoke 100'
          usage='Use for default text color'
        />
        <ColorBox
          colorName='whitesmoke'
          shade={1}
          displayName='Whitesmoke 70'
          usage='Use when you need text with lesser importance'
        />
        <ColorBox
          colorName='whitesmoke'
          shade={2}
          displayName='Whitesmoke 50'
          usage='Use as text in disabled components'
        />
        <ColorBox
          colorName='whitesmoke'
          shade={3}
          displayName='Whitesmoke 20'
          usage='Use as subtle borders, dividers'
        />
        <ColorBox
          colorName='whitesmoke'
          shade={4}
          displayName='Whitesmoke 10'
          usage='Use as very subtle backgrounds'
        />
      </section>
      <Divider />

      <h3>Typography</h3>
      <section>
        <h4>Guidelines</h4>
        <ul>
          <li>Avoid using text size smaller than 14px</li>
          <li>Don't underline words. For emphasis use bold text</li>
          <li>Don't use 2 different sizes of text in the same line</li>
        </ul>
      </section>
      <Divider />

      <h3>Border Radius</h3>
      <section>
        <h4>Guidelines</h4>
        Our design system contains various fixed units for radius of elements corners.
      </section>

      <Group gap='xl' pt='xl' pb='xl' align='flex-start'>
        <Stack gap='xs'>
          <div
            style={{
              width: '80px',
              height: '80px',
              border: '1px solid var(--mantine-color-feedback-7)',
              borderRadius: 'var(--mantine-radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            sm
          </div>
          <Text fw={500}>Small (4px)</Text>
          <Stack gap={4}>
            <Text size='sm' c='whitesmoke.1'>
              Forms
            </Text>
            <Text size='sm' c='whitesmoke.1'>
              Buttons
            </Text>
            <Text size='sm' c='whitesmoke.1'>
              Check boxes
            </Text>
            <Text size='sm' c='whitesmoke.1'>
              Selected mark
            </Text>
            <Text size='sm' c='whitesmoke.1'>
              Tooltips
            </Text>
          </Stack>
        </Stack>

        <Stack gap='xs'>
          <div
            style={{
              width: '80px',
              height: '80px',
              border: '1px solid var(--mantine-color-feedback-7)',
              borderRadius: 'var(--mantine-radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            md
          </div>
          <Text fw={500}>Medium (8px)</Text>
          <Stack gap={4}>
            <Text size='sm' c='whitesmoke.1'>
              Dropdown
            </Text>
            <Text size='sm' c='whitesmoke.1'>
              Popovers
            </Text>
            <Text size='sm' c='whitesmoke.1'>
              Light box
            </Text>
          </Stack>
        </Stack>

        <Stack gap='xs'>
          <div
            style={{
              width: '80px',
              height: '80px',
              border: '1px solid var(--mantine-color-feedback-7)',
              borderRadius: 'var(--mantine-radius-xl)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            xl
          </div>
          <Text fw={500}>Large (16px)</Text>
          <Stack gap={4}>
            <Text size='sm' c='whitesmoke.1'>
              Popups
            </Text>
          </Stack>
        </Stack>
      </Group>
      <Divider />

      <h2>Buttons</h2>
      <Stack gap='lg'>
        <Group gap='md'>
          <Button variant='filled' color='brand-cyan.5'>
            Click me
          </Button>
          <label>filled</label>
          <Text c='whitesmoke.1'>use as a primary button</Text>
        </Group>
        <Group gap='md'>
          <Button variant='outline'>Click me</Button>
          <label>outline</label>
          <Text c='whitesmoke.1'>use as a secodary button</Text>
        </Group>
        <Group gap='md'>
          <Button
            variant='gradient'
            gradient={{ deg: 135, from: 'brand-cyan.7', to: 'brand-green.5' }}
          >
            Click me
          </Button>
          <label>gradient</label>
        </Group>
        <Group gap='md'>
          <Button variant='light'>Click me</Button>
          <label>light</label>
        </Group>
        <Group gap='md'>
          <Button variant='subtle'>Click me</Button>
          <label>subtle</label>
        </Group>
        <Group gap='md'>
          <Button variant='transparent'>Click me</Button>
          <label>transparent</label>
        </Group>
        <Group gap='md'>
          <Button leftSection={<IconArrowLeft />} variant='filled' color='brand-cyan.5'>
            Previous
          </Button>
          <label>icon on the left</label>
        </Group>
        <Group gap='md'>
          <Button rightSection={<IconArrowRight />} variant='filled' color='brand-cyan.5'>
            Next
          </Button>
          <label>icon on the right</label>
        </Group>
      </Stack>

      <h2>Inputs with button</h2>
      <Items>
        <div>
          <InputWithButton />
          <label>search</label>
        </div>
      </Items>
    </Wrapper>
  );
}

export function InputWithButton(props: TextInputProps) {
  const theme = useMantineTheme();

  return (
    <TextInput
      radius='xl'
      size='md'
      placeholder='Search questions'
      rightSectionWidth={42}
      leftSection={<IconSearch size={18} stroke={1.5} />}
      rightSection={
        <ActionIcon size={32} radius='xl' color={theme.primaryColor} variant='filled'>
          <IconArrowRight size={18} stroke={1.5} />
        </ActionIcon>
      }
      {...props}
    />
  );
}
export default PageStory;
